import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Drawer,
  Empty,
  Grid,
  Input,
  Pagination,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
  ShopOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchStores } from '../store/slices/storesSlice';
import PageShell from '../components/Layout/PageShell';
import type { Store } from '../types';

const { Text } = Typography;

type SortPreset =
  | 'createdAt:desc'
  | 'createdAt:asc'
  | 'name:asc'
  | 'name:desc'
  | 'listings:desc'
  | 'listings:asc'
  | 'status:asc'
  | 'status:desc';

const SORT_OPTIONS: { value: SortPreset; label: string }[] = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'name:asc', label: 'Name A–Z' },
  { value: 'name:desc', label: 'Name Z–A' },
  { value: 'listings:desc', label: 'Most listings' },
  { value: 'listings:asc', label: 'Fewest listings' },
  { value: 'status:asc', label: 'Status A–Z' },
  { value: 'status:desc', label: 'Status Z–A' },
];

const FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All stores' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending verification' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suspended', label: 'Suspended' },
];

function categoryLabel(store: Store & { categories?: unknown }): string {
  const cats = store.categories as unknown;
  if (!Array.isArray(cats) || cats.length === 0) return 'No category';
  const first = cats[0] as { name?: string };
  return typeof first?.name === 'string' ? first.name : 'No category';
}

function statusTag(status: string | undefined, compact?: boolean) {
  if (!status) return <Tag color="default">UNKNOWN</Tag>;
  const color =
    status === 'approved' ? 'green' : status === 'pending' ? 'orange' : status === 'rejected' || status === 'suspended' ? 'red' : 'blue';
  const label = status.toUpperCase().replace(/_/g, ' ');
  return (
    <Tag color={color} className={compact ? 'm-0 max-w-full truncate px-1.5 py-0 text-[10px] leading-4' : undefined}>
      {label}
    </Tag>
  );
}

const Stores: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { stores, loading, pagination } = useAppSelector((state) => state.stores);
  const screens = Grid.useBreakpoint();
  /** Below md: compact search + bottom drawer for sort/filter (saves vertical space on phones). */
  const useCompactFilters = screens.md === false;
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortPreset, setSortPreset] = useState<SortPreset>('createdAt:desc');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchText]);

  const prevDebouncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevDebouncedRef.current === null) {
      prevDebouncedRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedRef.current !== debouncedSearch) {
      prevDebouncedRef.current = debouncedSearch;
      setPage(1);
    }
  }, [debouncedSearch]);

  const [sortBy, sortOrder] = useMemo(() => {
    const [a, b] = sortPreset.split(':') as [string, 'asc' | 'desc'];
    return [a, b] as const;
  }, [sortPreset]);

  useEffect(() => {
    dispatch(
      fetchStores({
        page,
        limit: pageSize,
        status: statusFilter || undefined,
        search: debouncedSearch || undefined,
        sortBy: sortBy as 'createdAt' | 'name' | 'listings' | 'status',
        sortOrder,
      })
    );
  }, [dispatch, page, pageSize, debouncedSearch, statusFilter, sortBy, sortOrder]);

  const onStatusChange = useCallback((v: string) => {
    setStatusFilter(v);
    setPage(1);
  }, []);

  const onSortChange = useCallback((v: SortPreset) => {
    setSortPreset(v);
    setPage(1);
  }, []);

  const onPaginationChange = useCallback((p: number, ps?: number) => {
    const nextSize = ps ?? pageSize;
    if (nextSize !== pageSize) {
      setPageSize(nextSize);
      setPage(1);
    } else {
      setPage(p);
    }
  }, [pageSize]);

  const total = pagination?.totalItems ?? 0;

  const columns = useMemo(
    () => [
      {
        title: 'Store',
        key: 'store',
        render: (_: unknown, record: Store) => (
          <div className="flex items-center gap-3 min-w-0">
            <Avatar src={record.logo} size={40} className="shrink-0 bg-slate-200">
              {(record.storeName || '?').charAt(0).toUpperCase()}
            </Avatar>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate">{record.storeName}</div>
              <div className="text-sm text-gray-500 truncate">{categoryLabel(record)}</div>
            </div>
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'verificationStatus',
        key: 'verificationStatus',
        width: 140,
        render: (status: string) => statusTag(status, false),
      },
      {
        title: 'Location',
        key: 'address',
        render: (_: unknown, record: Store) => (
          <div>
            <div className="font-medium text-slate-800">
              {record.address?.city}, {record.address?.state}
            </div>
            <div className="text-sm text-gray-500">{record.address?.country}</div>
          </div>
        ),
      },
      {
        title: 'Listings',
        dataIndex: 'stats',
        key: 'stats',
        width: 100,
        render: (stats: Store['stats'] | undefined) => stats?.totalListings ?? 0,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: (_: unknown, record: Store) => (
          <Space size="small" wrap>
            <Button type="link" className="!px-1" icon={<EyeOutlined />} onClick={() => navigate(`/stores/${record._id}`)}>
              View
            </Button>
            <Button type="link" className="!px-1" icon={<EditOutlined />} onClick={() => navigate(`/stores/${record._id}/edit`)}>
              Edit
            </Button>
          </Space>
        ),
      },
    ],
    [navigate]
  );

  return (
    <PageShell
      title="My stores"
      description="Search by name or location, narrow by status, and reorder the list. On phones, open Sort & filter for a full-width panel."
      titleEnd={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/stores/create')}
          className="w-full min-[480px]:w-auto border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] !text-white hover:!text-white shadow-md transition-shadow hover:shadow-lg"
        >
          Create store
        </Button>
      }
    >
      <Card className="vendor-card-flush border border-slate-200/80 shadow-sm bg-white rounded-xl overflow-hidden">
        <div className="border-b border-slate-100 bg-slate-50/50 px-3 py-2 sm:px-4">
          {useCompactFilters ? (
            <>
              <div className="flex w-full min-w-0 items-stretch gap-2">
                <Input
                  allowClear
                  size="middle"
                  placeholder="Search name or location"
                  prefix={<SearchOutlined className="text-slate-400" />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg"
                />
                <Badge dot={Boolean(statusFilter)} className="shrink-0">
                  <Button
                    type="default"
                    size="middle"
                    aria-label="Open sort and filter"
                    icon={<FilterOutlined />}
                    onClick={() => setFiltersDrawerOpen(true)}
                    className="border-slate-200 text-slate-700"
                  >
                    <span className="hidden min-[380px]:inline">Filters</span>
                  </Button>
                </Badge>
              </div>
              <Drawer
                title="Sort & filter"
                placement="bottom"
                height="auto"
                open={filtersDrawerOpen}
                onClose={() => setFiltersDrawerOpen(false)}
                rootClassName="stores-filter-drawer"
                styles={{ body: { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' } }}
              >
                <div className="flex flex-col gap-4">
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Sort by
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      value={sortPreset}
                      onChange={(v) => onSortChange(v as SortPreset)}
                      options={SORT_OPTIONS}
                      showSearch
                      optionFilterProp="label"
                      suffixIcon={<SortAscendingOutlined className="text-slate-500" />}
                    />
                  </div>
                  <div>
                    <Text type="secondary" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                    </Text>
                    <Select
                      size="large"
                      className="w-full"
                      placeholder="All stores"
                      allowClear
                      value={statusFilter || undefined}
                      onChange={(v) => onStatusChange(typeof v === 'string' ? v : '')}
                      options={FILTER_OPTIONS}
                      showSearch
                      optionFilterProp="label"
                      suffixIcon={<FilterOutlined className="text-slate-500" />}
                    />
                  </div>
                  <Button type="primary" block size="large" onClick={() => setFiltersDrawerOpen(false)}>
                    Done
                  </Button>
                </div>
              </Drawer>
            </>
          ) : (
            <Row gutter={[8, 8]} align="middle">
              <Col xs={24} sm={12} md={9} lg={9} xl={10}>
                <Input
                  allowClear
                  size="middle"
                  placeholder="Search by store name or location"
                  prefix={<SearchOutlined className="text-slate-400" />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="rounded-lg"
                />
              </Col>
              <Col xs={24} sm={12} md={8} lg={7} xl={7}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Sort by"
                  value={sortPreset}
                  onChange={onSortChange}
                  options={SORT_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                  suffixIcon={<SortAscendingOutlined className="text-slate-500" />}
                />
              </Col>
              <Col xs={24} sm={12} md={7} lg={8} xl={7}>
                <Select
                  size="middle"
                  className="w-full"
                  placeholder="Status"
                  allowClear
                  value={statusFilter || undefined}
                  onChange={(v) => onStatusChange(typeof v === 'string' ? v : '')}
                  options={FILTER_OPTIONS}
                  showSearch
                  optionFilterProp="label"
                  suffixIcon={<FilterOutlined className="text-slate-500" />}
                />
              </Col>
            </Row>
          )}
          <Text type="secondary" className="mt-1.5 block text-[11px] leading-snug sm:text-xs">
            {total === 0 && !loading
              ? 'No stores match your filters.'
              : loading
                ? 'Updating results…'
                : `${total} store${total === 1 ? '' : 's'} · this page ${stores.length}`}
          </Text>
        </div>

        <div className="hidden lg:block px-2 py-2 sm:px-3">
          <Table<Store>
            columns={columns}
            dataSource={stores}
            loading={loading}
            rowKey="_id"
            pagination={false}
            size="small"
            className="modern-table"
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No stores yet — create your first store to get started."
                />
              ),
            }}
          />
        </div>

        <div className="lg:hidden px-2 pb-2 pt-1 sm:px-3 sm:pb-3 sm:pt-2">
          {loading && stores.length === 0 ? (
            <div className="space-y-2.5 py-1">
              {[0, 1, 2].map((i) => (
                <Card key={i} className="rounded-xl border border-slate-200/80 shadow-sm" size="small">
                  <Skeleton active avatar={{ size: 52, shape: 'square' }} title={{ width: '55%' }} paragraph={{ rows: 2 }} />
                </Card>
              ))}
            </div>
          ) : stores.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No stores match your filters."
              className="py-8"
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/stores/create')}>
                Create store
              </Button>
            </Empty>
          ) : (
            <Spin spinning={loading} size="small" className="w-full [&_.ant-spin-nested-loading]:min-h-[4rem]">
              <ul className="m-0 list-none space-y-2.5 p-0 sm:space-y-3">
                {stores.map((store) => (
                  <li key={store._id}>
                    <Card
                      size="small"
                      className="vendor-store-card-mobile overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition-shadow hover:shadow-md"
                    >
                      <div className="flex flex-col gap-2.5">
                        <Avatar
                          src={store.logo}
                          size={52}
                          shape="square"
                          alt={store.storeName ? `${store.storeName} logo` : 'Store'}
                          className="!h-[52px] !w-[52px] shrink-0 rounded-xl border border-slate-100 bg-slate-100 text-base font-semibold text-[#0171BF] shadow-sm"
                        >
                          {(store.storeName || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <div className="min-w-0">
                          <Text strong className="block whitespace-normal break-words text-sm leading-snug text-slate-900">
                            {store.storeName}
                          </Text>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                            {statusTag(store.verificationStatus, true)}
                            <Text type="secondary" className="text-[11px] sm:text-xs">
                              <Text strong className="text-slate-700">
                                {store.stats?.totalListings ?? 0}
                              </Text>{' '}
                              listings
                            </Text>
                          </div>
                          <Text
                            type="secondary"
                            className="mt-0.5 flex items-start gap-1 text-[11px] leading-snug sm:text-xs"
                          >
                            <ShopOutlined className="mt-0.5 shrink-0 text-slate-400" aria-hidden />
                            <span className="min-w-0 break-words">
                              {categoryLabel(store)}
                              <span className="text-slate-300"> · </span>
                              {[store.address?.city, store.address?.state].filter(Boolean).join(', ') || '—'}
                            </span>
                          </Text>
                        </div>
                      </div>
                      <Divider className="!my-2.5 border-slate-100" />
                      <div className="flex gap-2">
                        <Button
                          size="middle"
                          className="min-h-9 flex-1 border-slate-200 font-medium text-slate-800"
                          icon={<EyeOutlined />}
                          onClick={() => navigate(`/stores/${store._id}`)}
                        >
                          View
                        </Button>
                        <Button
                          type="primary"
                          size="middle"
                          className="min-h-9 flex-1 border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] font-medium !text-white shadow-sm hover:!text-white hover:shadow-md"
                          icon={<EditOutlined />}
                          onClick={() => navigate(`/stores/${store._id}/edit`)}
                        >
                          Edit
                        </Button>
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            </Spin>
          )}
        </div>

        {total > 0 ? (
          <div className="flex flex-col gap-2 border-t border-slate-100 bg-white px-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-3 sm:py-2.5">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              pageSizeOptions={[10, 20, 50]}
              onChange={onPaginationChange}
              size="small"
              showTotal={(t, range) => `${range[0]}–${range[1]} of ${t}`}
              responsive
              className="stores-pagination flex flex-wrap justify-center lg:justify-start gap-y-2 text-xs sm:text-sm"
            />
          </div>
        ) : null}
      </Card>
    </PageShell>
  );
};

export default Stores;
